import { useState } from "react";
import { motion } from "framer-motion";
import {
  Menu,
  X,
  ArrowUpRight,
  Sparkles,
  FileText,
  Globe,
  Landmark,
  Wallet,
  MapPin,
  Calculator,
  UserPlus,
  LogIn,
  ShieldCheck,
  Store,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const LOGO_URL =
  "/assets/logo.png";

const SECTIONS = [
  {
    id: "aides-administratives",
    label: "Rubrique 01",
    icon: FileText,
    title: "Les aides administratives pour votre quotidien",
    subtitle: "Simplifiez vos démarches et gagnez du temps.",
    cards: [
      {
        id: "infogreffe",
        title: "Infogreffe.fr",
        description:
          "Rechercher des entreprises et obtenir des informations certifiées : Extrait Kbis, bénéficiaires effectifs, état d'endettement, actes et statuts, comptes annuels, procédures collectives.",
        href: "https://www.infogreffe.fr/",
        logo: "/assets/partners/infogreffe-fr.png",
      },
      {
        id: "urssaf",
        title: "Urssaf.fr",
        description:
          "Le site d'information et de services de l'Urssaf : vos débuts, embaucher et gérer un salarié, démarches, aide en cas de difficulté, attestation de vigilance.",
        href: "https://www.urssaf.fr/",
        logo: "/assets/partners/urssaf-fr.png",
      },
      {
        id: "insee",
        title: "Insee.fr",
        description:
          "L'Institut national de la statistique et des études économiques collecte, produit, analyse et diffuse des informations sur l'économie et la société françaises.",
        href: "https://www.insee.fr/",
        logo: "/assets/partners/insee.png",
      },
      {
        id: "inpi",
        title: "Inpi.fr",
        description:
          "Avec l'INPI France, protégez et valorisez vos créations : marques, brevets, dessins et modèles.",
        href: "https://www.inpi.fr/",
        logo: "/assets/partners/inpi-fr.png",
      },
      {
        id: "societe",
        title: "Societe.com",
        description:
          "Vérifier, surveiller et exporter toutes les données des entreprises et de leurs dirigeants : actionnaires, statuts, historique, comptes annuels, marques.",
        href: "https://www.societe.com/",
        logo: "/assets/partners/societe-com.png",
      },
      {
        id: "impots-gouv",
        title: "Impots.gouv.fr",
        description:
          "Accédez à votre espace professionnel pour déclarer, payer et suivre l'ensemble de vos obligations fiscales en ligne.",
        href: "https://www.impots.gouv.fr/",
        logo: "/assets/partners/impots-gouv-fr.png",
      },
    ],
  },
  {
    id: "createurs-sites",
    label: "Rubrique 02",
    icon: Globe,
    title: "Les services de créations de pages et de sites Internet professionnels",
    subtitle: "Lancez votre présence en ligne rapidement et simplement.",
    cards: [
      {
        id: "mapageentreprise",
        title: "MaPageEntreprise.fr",
        description: "Créateur de site web et de page internet, simple d'utilisation.",
        href: "https://mapageentreprise.fr/",
        logo: "/assets/partners/mapageentreprise-fr.png",
      },
    ],
  },
  {
    id: "banques-en-ligne",
    label: "Rubrique 03",
    icon: Landmark,
    title: "Les banques en ligne",
    subtitle: "Ouvrez votre compte pro en quelques minutes.",
    cards: [
      {
        id: "qonto",
        title: "Qonto.com",
        description:
          "Compte pro tout-en-un : facturation électronique, création d'entreprise et gestion quotidienne réunies sur une seule plateforme.",
        href: "https://qonto.com/",
        logo: "/assets/partners/qonto-com.png",
      },
      {
        id: "pennylane",
        title: "Pennylane.com",
        description:
          "Le Compte Pro intégré à votre comptabilité : synchronisez vos opérations bancaires et votre comptabilité en temps réel.",
        href: "https://www.pennylane.com/",
        logo: "/assets/partners/pennylane-com.png",
      },
      {
        id: "hellobankpro",
        title: "Hellobankpro.fr",
        description:
          "La banque en ligne préférée des pros : compte pro, assurances, crédit pro, encaissement et facturation — la seule vraie banque en ligne qui répond à tous vos besoins.",
        href: "https://www.hellobankpro.fr/",
        logo: "/assets/partners/hellobankpro-fr.png",
      },
      {
        id: "boursobank",
        title: "BoursoBank Professionnels",
        description:
          "Bourso Business : compte bancaire gratuit pour indépendants, carte business gratuite, assurance, épargne pro et solutions de paiement en partenariat.",
        href: "https://www.boursobank.com/professionnels",
        logo: "/assets/partners/boursobank-com.png",
      },
      {
        id: "shine",
        title: "Shine.fr",
        description:
          "Compte pro et facturation électronique : tout pour lancer et piloter votre activité — facturation conforme, encaissements simplifiés et comptabilité automatisée, sans engagement.",
        href: "https://www.shine.fr/",
        logo: "/assets/partners/shine-fr.png",
      },
    ],
  },
  {
    id: "services-paie",
    label: "Rubrique 04",
    icon: Wallet,
    title: "Les services de paie",
    subtitle: "Externalisez la paie de vos collaborateurs en toute sérénité.",
    cards: [
      {
        id: "bestpaye",
        title: "Bestpaye.fr",
        description:
          "Générez vos bulletins de salaire, contrôlez et télétransmettez vos fichiers DSN pour déclarer et payer vos cotisations sociales, et éditez tous les états de comptabilité nécessaires depuis un seul compte.",
        href: "https://bestpaye.fr/",
        logo: "/assets/partners/bestpaye-fr.png",
      },
      {
        id: "payfit",
        title: "Payfit.com",
        description:
          "Logiciel de paie et RH : paie, congés, entretiens — prêts à l'emploi. PayFit est fiable, rapide, automatisé et accessible à tous.",
        href: "https://payfit.com/fr/",
        logo: "/assets/partners/payfit-com.png",
      },
      {
        id: "mapaye",
        title: "Mapaye.fr",
        description:
          "Externalisez votre paie et sa complexité administrative. MaPaye est un service hybride associant un logiciel de paie en ligne et un gestionnaire de paie dédié.",
        href: "https://www.mapaye.fr/",
        logo: "/assets/partners/mapaye-fr.png",
      },
    ],
  },
  {
    id: "domiciliation",
    label: "Rubrique 05",
    icon: MapPin,
    title: "Les centres de domiciliation",
    subtitle: "Obtenez une adresse professionnelle prestigieuse.",
    cards: [
      {
        id: "digidom",
        title: "Digidom.pro",
        description:
          "Domiciliez et créez votre entreprise en toute simplicité avec Digidom.",
        href: "https://www.digidom.pro/",
        logo: "/assets/partners/digidom-pro.png",
      },
      {
        id: "sedomicilier",
        title: "Sedomicilier.fr",
        description:
          "N°1 de la domiciliation en France : simple, rapide et 100 % en ligne. Votre contrat en moins de 4h.",
        href: "https://sedomicilier.fr/",
        logo: "/assets/partners/sedomicilier-fr.png",
      },
      {
        id: "sofradom",
        title: "Province.sofradom.fr",
        description:
          "Domiciliation d'entreprises partout en France : 17 régions et plus de 150 villes sur tout le territoire.",
        href: "https://province.sofradom.fr/",
        logo: "/assets/partners/sofradom-fr.png",
      },
      {
        id: "ma-domiciliation",
        title: "Ma-domiciliation.fr",
        description:
          "La domiciliation d'entreprise nouvelle génération : une adresse professionnelle pour domicilier votre entreprise partout en France.",
        href: "https://ma-domiciliation.fr/",
        logo: "/assets/partners/ma-domiciliation-fr.png",
      },
    ],
  },
  {
    id: "experts-comptables",
    label: "Rubrique 06",
    icon: Calculator,
    title: "Les experts-comptables",
    subtitle: "Confiez votre comptabilité à un professionnel de confiance.",
    cards: [
      {
        id: "legalstart-comptastart",
        title: "Legalstart / Comptastart",
        description:
          "Toutes vos démarches juridiques et comptables au même endroit. Questionnaire en ligne, traitement en 48h, plus de 50 juristes-formalistes basés en France, à un rapport qualité/prix imbattable.",
        href: "https://www.legalstart.fr/comptastart/",
        logo: "/assets/partners/legalstart-fr.jpg",
      },
      {
        id: "dougs",
        title: "Dougs.fr",
        description:
          "L'expert-comptable en ligne qui vous simplifie la vie : de la création de votre entreprise à la constitution de votre patrimoine, en optimisant votre fiscalité à chaque étape.",
        href: "https://www.dougs.fr/",
        logo: "/assets/partners/dougs-fr.png",
      },
      {
        id: "legalplace",
        title: "Legalplace.fr",
        description:
          "Un expert-comptable réactif toujours à vos côtés. Conseils illimités, logiciel simple sans saisie manuelle, ultra réactif par tchat, téléphone ou visio.",
        href: "https://www.legalplace.fr/",
        logo: "/assets/partners/legalplace-fr.png",
      },
    ],
  },
];

/* ---------- SITE INTERNET — Espace inscription + Espace client ---------- */
function SiteInternetSection() {
  const [payLoading, setPayLoading] = useState(false);
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const startCheckout = async () => {
    setPayLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/payments/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origin_url: window.location.origin,
          template: "guide_pro",
        }),
      });
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert(
          data?.detail ||
            "Le paiement est momentanément indisponible. Merci de réessayer."
        );
        setPayLoading(false);
      }
    } catch (e) {
      alert("Erreur réseau, réessayez plus tard.");
      setPayLoading(false);
    }
  };

  return (
    <section
      id="site-internet"
      data-testid="section-site-internet"
      className="relative scroll-mt-24 bg-white pt-28 pb-14 sm:pt-36 sm:pb-20"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
        <div className="h-1 w-24 rounded-b-full bg-gradient-to-r from-[#1E2A5E] to-[#2FA398]" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Cards — centered horizontally on all breakpoints */}
        <div className="mx-auto grid max-w-4xl grid-cols-1 justify-center gap-6 md:grid-cols-2">
          {/* Espace inscription */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5 }}
            className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_4px_20px_-4px_rgba(30,42,94,0.08)] ring-1 ring-slate-100/50 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#1E2A5E]/60 hover:shadow-[0_20px_40px_-12px_rgba(30,42,94,0.18)] hover:ring-[#1E2A5E]/20"
          >
            <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-[#1E2A5E] transition-transform duration-300 group-hover:scale-x-100" />
            <div>
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-[#1E2A5E] shadow-sm">
                <UserPlus className="h-7 w-7 text-white" strokeWidth={2} />
              </div>
              <span className="mb-3 inline-block rounded-full bg-[#1E2A5E]/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#1E2A5E]">
                Espace inscription
              </span>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Abonnez-vous pour <span className="font-bold text-[#1E2A5E]">9,90&nbsp;€/mois</span>, sans engagement et résiliable à tout moment. Votre site internet d&apos;entreprise, en ligne sur Google, pour le prix d&apos;un café par mois.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1E2A5E]" />
                  Paiement sécurisé en ligne (Stripe)
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1E2A5E]" />
                  Accès instantané à votre espace après paiement
                </li>
              </ul>
            </div>
            <button
              type="button"
              onClick={startCheckout}
              disabled={payLoading}
              data-testid="cta-inscription"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E2A5E] px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#2A3B7A] disabled:cursor-wait disabled:opacity-70"
            >
              {payLoading ? "Redirection…" : "S'inscrire"}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </motion.div>

          {/* Espace client */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_4px_20px_-4px_rgba(30,42,94,0.08)] ring-1 ring-slate-100/50 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#2FA398]/60 hover:shadow-[0_20px_40px_-12px_rgba(30,42,94,0.18)] hover:ring-[#2FA398]/20"
          >
            <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-[#2FA398] transition-transform duration-300 group-hover:scale-x-100" />
            <div>
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-[#2FA398] shadow-sm">
                <LogIn className="h-7 w-7 text-white" strokeWidth={2} />
              </div>
              <span className="mb-3 inline-block rounded-full bg-[#2FA398]/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#2FA398]">
                Espace client
              </span>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Accédez à votre tableau de bord pour modifier votre site internet entreprise, ajouter vos horaires, photos, services ou publier votre site sur Google en un clic.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-700">
                <li className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2FA398]" />
                  Connexion sécurisée par email ou Google
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2FA398]" />
                  Modifiez, activez ou désactivez votre page à tout moment
                </li>
              </ul>
            </div>
            <Link
              to="/mon-compte-client"
              data-testid="cta-connexion"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2FA398] px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#26857C]"
            >
              Connexion à mon espace
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HEADER ---------- */
function Header({ onMenuClick }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 sm:h-28 sm:px-6 lg:px-8">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Le Guide des Entrepreneurs"
            className="h-16 w-auto sm:h-20 lg:h-24"
          />
        </a>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="menu-trigger"
              onClick={onMenuClick}
              className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#1E2A5E] shadow-sm transition-all hover:border-[#2FA398] hover:shadow-md sm:px-5"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" strokeWidth={2.5} />
              <span className="hidden sm:inline">Menu</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full border-l border-slate-200 bg-white p-0 sm:max-w-md"
          >
            <SheetHeader className="border-b border-slate-100 px-6 py-5">
              <SheetTitle className="text-left text-lg font-bold text-[#1E2A5E]">
                Rubriques
              </SheetTitle>
            </SheetHeader>
            <nav
              data-testid="menu-nav"
              className="flex flex-col gap-1 p-4"
            >
              <a
                href="#site-internet"
                onClick={() => setMenuOpen(false)}
                data-testid="menu-link-site-internet"
                className="group flex items-start justify-between gap-4 rounded-xl px-4 py-4 transition-colors hover:bg-[#F5F8FF]"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 font-mono text-xs font-semibold text-[#2FA398]">★</span>
                  <span className="text-[15px] font-semibold leading-snug text-[#1E2A5E]">
                    Site internet
                  </span>
                </div>
                <ArrowUpRight
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#2FA398]"
                  strokeWidth={2}
                />
              </a>
              {SECTIONS.map((section, idx) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`menu-link-${section.id}`}
                  className="group flex items-start justify-between gap-4 rounded-xl px-4 py-4 transition-colors hover:bg-[#F5F8FF]"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 font-mono text-xs font-semibold text-[#2FA398]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[15px] font-semibold leading-snug text-[#1E2A5E]">
                      {section.title}
                    </span>
                  </div>
                  <ArrowUpRight
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#2FA398]"
                    strokeWidth={2}
                  />
                </a>
              ))}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative overflow-hidden bg-gradient-to-b from-[#F5F8FF] to-white pb-20 pt-32 sm:pt-40"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-96 w-[900px] -translate-x-1/2 rounded-full bg-[#2FA398]/10 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#2FA398]/30 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#2FA398] shadow-sm"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Votre allié entrepreneur
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="hero-title"
          className="text-4xl font-extrabold leading-[1.1] tracking-tight text-[#1E2A5E] sm:text-5xl lg:text-6xl"
        >
          Toutes les ressources pour{" "}
          <span className="text-[#2FA398]">entreprendre sereinement</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          Découvrez notre sélection de partenaires et de services pour vous
          accompagner à chaque étape de votre aventure entrepreneuriale.
        </motion.p>
      </div>
    </section>
  );
}

/* ---------- CARDS ---------- */
function PlaceholderCard() {
  return (
    <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#2FA398] shadow-sm">
        <Sparkles className="h-4 w-4" />
      </div>
      <p className="text-sm font-semibold text-[#1E2A5E]">
        Bientôt disponible
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Nos partenaires arrivent prochainement
      </p>
    </div>
  );
}

function PartnerCard({ card }) {
  const [logoError, setLogoError] = useState(false);
  const showLogo = card.logo && !logoError;
  return (
    <a
      href={card.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`card-${card.id}`}
      className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_4px_20px_-4px_rgba(30,42,94,0.08)] ring-1 ring-slate-100/50 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#2FA398]/60 hover:shadow-[0_20px_40px_-12px_rgba(30,42,94,0.18)] hover:ring-[#2FA398]/20"
    >
      {/* Barre décorative turquoise au sommet */}
      <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#1E2A5E] to-[#2FA398] transition-transform duration-300 group-hover:scale-x-100" />

      <div>
        <div className="mb-5 flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
          {showLogo ? (
            <img
              src={card.logo}
              alt={card.title}
              onError={() => setLogoError(true)}
              className="h-9 w-9 object-contain"
            />
          ) : (
            <span className="text-lg font-extrabold text-[#1E2A5E]">
              {card.title.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-[#1E2A5E]">{card.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {card.description}
        </p>
      </div>
      <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2FA398]">
        Consulter
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </a>
  );
}

function Section({ section, index }) {
  const cards = section.cards ?? [];
  const displayCards = cards.length > 0 ? cards : [null, null, null];
  const Icon = section.icon ?? Sparkles;
  const isAlt = index % 2 === 1;

  return (
    <section
      id={section.id}
      data-testid={`section-${section.id}`}
      className={`relative scroll-mt-24 py-20 sm:py-28 ${
        isAlt ? "bg-[#F5F8FF]" : "bg-white"
      }`}
    >
      {/* Top decorative divider */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
        <div className="h-1 w-24 rounded-b-full bg-gradient-to-r from-[#1E2A5E] to-[#2FA398]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12 flex flex-col items-start gap-5 sm:mb-16"
        >
          {/* Badge : icon + numéro */}
          <div className="flex items-center gap-3">
            <div
              data-testid={`section-badge-${section.id}`}
              className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E2A5E] to-[#2FA398] shadow-lg shadow-[#1E2A5E]/20"
            >
              <Icon className="h-7 w-7 text-white" strokeWidth={2} />
            </div>
            <span className="rounded-full border border-[#2FA398]/30 bg-white px-3 py-1 font-mono text-xs font-bold uppercase tracking-widest text-[#2FA398]">
              {section.label ?? `Rubrique ${String(index + 1).padStart(2, "0")}`}
            </span>
          </div>

          {/* Titre imposant + trait latéral */}
          <div className="relative w-full border-l-4 border-[#2FA398] pl-5">
            <h2
              data-testid={`section-title-${section.id}`}
              className="text-3xl font-extrabold leading-[1.15] tracking-tight text-[#1E2A5E] sm:text-4xl lg:text-5xl"
            >
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="mt-3 max-w-2xl text-base text-slate-600 sm:text-lg">
                {section.subtitle}
              </p>
            )}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              {card ? <PartnerCard card={card} /> : <PlaceholderCard />}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-slate-200 bg-white"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-6 sm:flex-row sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Le Guide des Entrepreneurs"
            className="h-10 w-auto"
          />
        </div>
        <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-semibold">
          <Link
            to="/cgv"
            data-testid="footer-link-cgv"
            className="text-[#1E2A5E] transition-colors hover:text-[#2FA398]"
          >
            Conditions Générales de Vente
          </Link>
          <span className="text-slate-300" aria-hidden="true">•</span>
          <Link
            to="/mentions-legales"
            data-testid="footer-link-mentions-legales"
            className="text-[#1E2A5E] transition-colors hover:text-[#2FA398]"
          >
            Mentions Légales
          </Link>
        </nav>
        <p className="text-[10px] text-slate-500 sm:text-[11px]">
          Ce site est créé par{" "}
          <a
            href="https://leguidedesentrepreneurs.fr/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#1E2A5E] transition-colors hover:text-[#2FA398]"
          >
            Le Guide des Entrepreneurs.fr
          </a>
          , tous droits réservés.
        </p>
      </div>
    </footer>
  );
}

/* ---------- PAGE ---------- */
export default function Home() {
  const [, setMenuOpen] = useState(false);
  return (
    <div
      data-testid="home-page"
      className="min-h-screen bg-white font-sans text-[#1E2A5E] antialiased"
      style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" }}
    >
      <Header onMenuClick={() => setMenuOpen(true)} />
      <main>
        <SiteInternetSection />
        <Hero />
        {SECTIONS.map((section, idx) => (
          <Section key={section.id} section={section} index={idx} />
        ))}
      </main>
      <Footer />
    </div>
  );
}
